import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  buildEmbeddedSignupOptions,
  DEFAULT_EMBEDDED_SIGNUP_VERSION,
  EMBEDDED_SIGNUP_VERSIONS,
  getUserActivationSnapshot,
  type EmbeddedSignupVersion,
} from '../components/embeddedSignupOptions';
import {
  invokeEmbeddedSignupLogin,
  type EmbeddedSignupSdk,
  type MetaLoginResponse,
} from '../components/embeddedSignupSdk';

interface DiagnosticMetaSdk extends EmbeddedSignupSdk {
  init: (options: Record<string, unknown>) => void;
  getLoginStatus?: (callback: (response: MetaLoginResponse) => void) => void;
}

type DiagnosticWindow = Window &
  typeof globalThis & {
    FB?: DiagnosticMetaSdk;
    fbAsyncInit?: () => void;
  };

interface DiagnosticLogEntry {
  id: number;
  time: string;
  stage: string;
  details: Record<string, unknown>;
}

const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const SDK_SCRIPT_ID = 'meta-sdk-diagnostic-jssdk';
const SDK_LOAD_TIMEOUT_MS = 15_000;
const STATUS_CALLBACK_TIMEOUT_MS = 5_000;
const LOGIN_CALLBACK_TIMEOUT_MS = 15_000;
const STAGING_HOST = 'staging.digitalcloud360.net';

export const MetaSdkDiagnostic: React.FC = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [appId, setAppId] = useState(params.get('app_id') || '2459452254454831');
  const [configId, setConfigId] = useState(params.get('config_id') || '');
  const [graphVersion, setGraphVersion] = useState(params.get('graph_version') || 'v24.0');
  const [embeddedSignupVersion, setEmbeddedSignupVersion] =
    useState<EmbeddedSignupVersion>(() => {
      const requested = params.get('es_version');
      return EMBEDDED_SIGNUP_VERSIONS.includes(requested as EmbeddedSignupVersion)
        ? (requested as EmbeddedSignupVersion)
        : DEFAULT_EMBEDDED_SIGNUP_VERSION;
    });
  const [sdkState, setSdkState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [probeStatusAroundLogin, setProbeStatusAroundLogin] = useState(false);
  const [logs, setLogs] = useState<DiagnosticLogEntry[]>([]);
  const sdkRef = useRef<DiagnosticMetaSdk | null>(null);
  const logSequenceRef = useRef(0);
  const timersRef = useRef<Set<number>>(new Set());
  const popupRef = useRef<Window | null>(null);

  const addLog = (stage: string, details: Record<string, unknown> = {}) => {
    const entry = {
      id: ++logSequenceRef.current,
      time: new Date().toISOString(),
      stage,
      details,
    };
    console.info('[Meta SDK Diagnostic]', stage, details);
    setLogs((current) => [...current, entry]);
  };

  const addTimer = (callback: () => void, timeout: number) => {
    const timerId = window.setTimeout(() => {
      timersRef.current.delete(timerId);
      callback();
    }, timeout);
    timersRef.current.add(timerId);
    return timerId;
  };

  const clearTimer = (timerId: number | undefined) => {
    if (timerId === undefined) return;
    window.clearTimeout(timerId);
    timersRef.current.delete(timerId);
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.endsWith('facebook.com')) return;

      try {
        const payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
        addLog('embedded_signup_event', {
          origin: event.origin,
          event: payload.event || 'unknown',
          version: payload.version || 'unknown',
          hasCurrentStep: Boolean(payload.data?.current_step),
          hasWabaId: Boolean(payload.data?.waba_id),
          hasPhoneNumberId: Boolean(payload.data?.phone_number_id),
        });
      } catch {
        // Other Facebook iframe messages are expected and deliberately ignored.
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      timersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      timersRef.current.clear();
    };
  }, []);

  const loadSdk = () => {
    if (!appId.trim()) {
      addLog('sdk_init_rejected', { reason: 'missing_app_id' });
      return;
    }

    setSdkState('loading');
    const diagnosticWindow = window as DiagnosticWindow;
    const existingSdk = diagnosticWindow.FB;

    const initializeSdk = (sdk: DiagnosticMetaSdk, source: string) => {
      try {
        sdk.init({
          appId: appId.trim(),
          autoLogAppEvents: true,
          xfbml: true,
          version: graphVersion.trim(),
        });
        sdkRef.current = sdk;
        setSdkState('ready');
        addLog('sdk_initialized', {
          source,
          sdkUrl: SDK_URL,
          graphVersion: graphVersion.trim(),
          hasInit: typeof sdk.init === 'function',
          hasLogin: typeof sdk.login === 'function',
          hasGetLoginStatus: typeof sdk.getLoginStatus === 'function',
        });
      } catch (error) {
        setSdkState('error');
        addLog('sdk_init_failed', {
          errorType: error instanceof Error ? error.name : typeof error,
        });
      }
    };

    if (existingSdk) {
      initializeSdk(existingSdk, 'existing_window_fb');
      return;
    }

    let settled = false;
    const loadTimeoutId = addTimer(() => {
      if (settled) return;
      settled = true;
      setSdkState('error');
      addLog('sdk_load_timeout', { timeoutMs: SDK_LOAD_TIMEOUT_MS });
    }, SDK_LOAD_TIMEOUT_MS);

    const previousAsyncInit = diagnosticWindow.fbAsyncInit;
    diagnosticWindow.fbAsyncInit = () => {
      previousAsyncInit?.();
      if (settled) return;
      settled = true;
      clearTimer(loadTimeoutId);
      if (!diagnosticWindow.FB) {
        setSdkState('error');
        addLog('sdk_async_init_without_fb');
        return;
      }
      addLog('fb_async_init_called');
      initializeSdk(diagnosticWindow.FB, 'fbAsyncInit');
    };

    const existingScript = document.getElementById(SDK_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      addLog('sdk_script_already_present', { src: existingScript.src });
      return;
    }

    const script = document.createElement('script');
    script.id = SDK_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = SDK_URL;
    script.onload = () => addLog('sdk_script_load_event', { hasWindowFb: Boolean(diagnosticWindow.FB) });
    script.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimer(loadTimeoutId);
      setSdkState('error');
      addLog('sdk_script_error');
    };
    addLog('sdk_script_appending', { src: SDK_URL });
    document.body.appendChild(script);
  };

  const invokeStatusCheck = (stage: string) => {
    const sdk = sdkRef.current;
    if (!sdk || typeof sdk.getLoginStatus !== 'function') {
      addLog(`${stage}_unavailable`, { hasSdk: Boolean(sdk) });
      return;
    }

    let callbackReceived = false;
    const timeoutId = addTimer(() => {
      if (!callbackReceived) {
        addLog(`${stage}_timeout`, { timeoutMs: STATUS_CALLBACK_TIMEOUT_MS });
      }
    }, STATUS_CALLBACK_TIMEOUT_MS);

    addLog(`${stage}_invoking`);
    try {
      sdk.getLoginStatus((response) => {
        callbackReceived = true;
        clearTimer(timeoutId);
        addLog(`${stage}_callback`, {
          status: response.status || 'unknown',
          hasAuthResponse: Boolean(response.authResponse),
        });
      });
      addLog(`${stage}_invoked`);
    } catch (error) {
      callbackReceived = true;
      clearTimer(timeoutId);
      addLog(`${stage}_threw`, {
        errorType: error instanceof Error ? error.name : typeof error,
      });
    }
  };

  const runLoginDiagnostic = (event: React.MouseEvent<HTMLButtonElement>) => {
    const sdk = sdkRef.current;
    if (!sdk || sdkState !== 'ready') {
      addLog('login_rejected', { reason: 'sdk_not_ready' });
      return;
    }
    if (!configId.trim()) {
      addLog('login_rejected', { reason: 'missing_config_id' });
      return;
    }

    const activationBefore = getUserActivationSnapshot(event.nativeEvent);
    const loginOptions = buildEmbeddedSignupOptions(configId.trim(), embeddedSignupVersion);
    let loginCallbackReceived = false;
    let capturedPopup = false;

    addLog('login_activation_before', { ...activationBefore });
    if (probeStatusAroundLogin) {
      invokeStatusCheck('status_before_login');
    }
    addLog('fb_login_invoking', {
      embeddedSignupVersion,
      sessionInfoVersion: loginOptions.extras.sessionInfoVersion,
      responseType: loginOptions.response_type,
      overrideDefaultResponseType: loginOptions.override_default_response_type,
      hasConfigId: Boolean(loginOptions.config_id),
    });

    const loginTimeoutId = addTimer(() => {
      if (loginCallbackReceived) return;
      addLog('fb_login_callback_timeout', {
        timeoutMs: LOGIN_CALLBACK_TIMEOUT_MS,
        popupCaptured: capturedPopup,
        popupClosed: popupRef.current ? popupRef.current.closed : null,
        activation: getUserActivationSnapshot(),
      });
      invokeStatusCheck('status_on_login_timeout');
    }, LOGIN_CALLBACK_TIMEOUT_MS);

    try {
      addLog('login_activation_at_invoke', {
        ...getUserActivationSnapshot(event.nativeEvent),
      });
      invokeEmbeddedSignupLogin(
        sdk,
        (response) => {
          loginCallbackReceived = true;
          clearTimer(loginTimeoutId);
          addLog('fb_login_callback', {
            status: response.status || 'unknown',
            hasAuthResponse: Boolean(response.authResponse),
            hasAuthorizationCode: Boolean(response.authResponse?.code),
          });
        },
        loginOptions,
        ({ popup, popupCreated, target }) => {
          capturedPopup = popupCreated;
          popupRef.current = popup;
          addLog('window_open_intercepted', {
            popupCreated,
            target,
          });
        }
      );
      addLog('fb_login_invoked', {
        popupCaptured: capturedPopup,
        activation: getUserActivationSnapshot(event.nativeEvent),
      });
    } catch (error) {
      loginCallbackReceived = true;
      clearTimer(loginTimeoutId);
      addLog('fb_login_threw', {
        errorType: error instanceof Error ? error.name : typeof error,
      });
    }

    if (probeStatusAroundLogin) {
      invokeStatusCheck('status_after_login');
    }
  };

  const hostAllowed =
    window.location.hostname === STAGING_HOST ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const optionsPreview = configId.trim()
    ? buildEmbeddedSignupOptions(configId.trim(), embeddedSignupVersion)
    : null;

  if (!hostAllowed) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Ce diagnostic est volontairement limité au staging et au développement local.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Diagnostic isolé du SDK Meta
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Cette page ne crée aucune session Embedded Signup dans le backend Digital Cloud 360 et
            n’appelle jamais les endpoints <code>start/</code> ou <code>complete/</code>.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Initialisez d’abord le SDK, puis lancez chaque version avec un clic utilisateur distinct.
            Les identifiants saisis sont des identifiants publics Meta et ne sont pas persistés.
          </Typography>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <TextField
              label="Meta App ID"
              value={appId}
              onChange={(event) => setAppId(event.target.value)}
              disabled={sdkState === 'loading' || sdkState === 'ready'}
            />
            <TextField
              label="Embedded Signup Config ID"
              value={configId}
              onChange={(event) => setConfigId(event.target.value)}
            />
            <TextField
              label="Graph API version"
              value={graphVersion}
              onChange={(event) => setGraphVersion(event.target.value)}
              disabled={sdkState === 'loading' || sdkState === 'ready'}
            />
            <FormControl>
              <InputLabel id="embedded-signup-version-label">Embedded Signup version</InputLabel>
              <Select
                labelId="embedded-signup-version-label"
                label="Embedded Signup version"
                value={embeddedSignupVersion}
                onChange={(event) =>
                  setEmbeddedSignupVersion(event.target.value as EmbeddedSignupVersion)
                }
              >
                {EMBEDDED_SIGNUP_VERSIONS.map((version) => (
                  <MenuItem key={version} value={version}>
                    {version}{version === DEFAULT_EMBEDDED_SIGNUP_VERSION ? ' — recommandée' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={probeStatusAroundLogin}
                  onChange={(event) => setProbeStatusAroundLogin(event.target.checked)}
                />
              }
              label="Sonder FB.getLoginStatus avant et après FB.login (désactivé pour le test pur)"
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Button variant="outlined" onClick={loadSdk} disabled={sdkState === 'loading' || sdkState === 'ready'}>
                1. Initialiser le SDK
              </Button>
              <Button
                variant="outlined"
                onClick={() => invokeStatusCheck('manual_status')}
                disabled={sdkState !== 'ready'}
              >
                2. Tester FB.getLoginStatus
              </Button>
              <Button
                variant="contained"
                onClick={runLoginDiagnostic}
                disabled={sdkState !== 'ready' || !configId.trim()}
              >
                3. Tester FB.login ({embeddedSignupVersion})
              </Button>
            </Stack>

            <Typography variant="body2">
              État SDK : <strong>{sdkState}</strong>
            </Typography>
            <Box component="pre" sx={{ m: 0, p: 2, bgcolor: 'grey.100', overflow: 'auto' }}>
              {JSON.stringify(optionsPreview, null, 2)}
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Journal sécurisé</Typography>
            <Button size="small" onClick={() => setLogs([])}>
              Effacer
            </Button>
          </Stack>
          {logs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucun événement pour le moment.
            </Typography>
          ) : (
            <Box component="pre" sx={{ m: 0, p: 2, bgcolor: '#111827', color: '#e5e7eb', overflow: 'auto' }}>
              {logs
                .map((entry) => `${entry.time} ${entry.stage} ${JSON.stringify(entry.details)}`)
                .join('\n')}
            </Box>
          )}
        </Paper>
      </Stack>
    </Container>
  );
};
